import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../utils/api';
import VendorContacts from './VendorContacts';
import VendorInfoTab from './VendorInfoTab';

const VendorDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('info');

  useEffect(() => {
    fetchVendor();
  }, [id]);

  const fetchVendor = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/vendors/${id}`);
      setVendor(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching vendor:', err);
      setError('Failed to load vendor details. The vendor may not exist or the server is unavailable.');
    } finally {
      setLoading(false);
    }
  };

  // Callback function to handle vendor updates from child components
  const handleVendorUpdate = (updatedVendor) => {
    setVendor(updatedVendor);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="w-12 h-12 border-t-2 border-b-2 border-indigo-500 rounded-full animate-spin"></div>
        <p className="mt-4 text-indigo-700">Loading vendor details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-700 bg-red-50 rounded-md border-l-4 border-red-500">
        <p>{error}</p>
        <div className="mt-4">
          <Link
            to="/vendors"
            className="text-red-700 underline"
          >
            Return to Vendor List
          </Link>
        </div>
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="p-4 text-gray-700 bg-gray-100 rounded-md">
        <p>Vendor not found.</p>
        <div className="mt-4">
          <Link
            to="/vendors"
            className="text-indigo-600 hover:text-indigo-900"
          >
            Return to Vendor List
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6">
      {/* Header - Indigo Theme */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg border-l-4 border-indigo-500">
        <div className="px-4 py-5 sm:px-6 bg-indigo-50 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold text-indigo-900">
              {vendor.name}
              <>
                {vendor.is_preferred && (
                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" title="Preferred Vendor">Preferred</span>
                )}
                {!vendor.is_active && (
                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    Inactive
                  </span>
                )}
              </>
            </h1>
          </div>
          <div className="flex space-x-3 items-center">
            <Link
              to="/vendors"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Back to Vendors
            </Link>
          </div>
        </div>
      </div>
      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mt-4">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('info')}
            className={`$${
              activeTab === 'info'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Vendor Information
          </button>
          <button
            onClick={() => setActiveTab('contacts')}
            className={`$${
              activeTab === 'contacts'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Contacts
          </button>
        </nav>
      </div>
      {/* Vendor Info Tab */}
      {activeTab === 'info' && (
        <VendorInfoTab vendor={vendor} onVendorUpdate={handleVendorUpdate} />
      )}
      {/* Contacts Tab */}
      {activeTab === 'contacts' && (
        <VendorContacts vendorId={id} />
      )}
    </div>
  );
};

export default VendorDetail; 